import React, { useMemo, useState } from "react";
import {
  useCreateMembershipBandMutation,
  useDeleteMembershipBandMutation,
  useGetAdminMembershipBandsQuery,
  useUpdateMembershipBandMutation,
  MembershipBand,
} from "../api/membershipBandsApi";
import { useGetUserCategoriesQuery } from "../api/userCategoriesApi";

const empty = {
  user_category_id: 0,
  label: "",
  min_years: 0,
  max_years: null as number | null,
  is_active: true,
};

export default function MembershipBandsPage() {
  const [q, setQ] = useState("");
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined);

  const [editing, setEditing] = useState<MembershipBand | null>(null);
  const [form, setForm] = useState<any>(empty);

  const { data: categoriesRes } = useGetUserCategoriesQuery();
  const categories = categoriesRes?.data ?? [];

  const {
    data: page,
    isLoading,
    error,
    refetch,
  } = useGetAdminMembershipBandsQuery({
    q: q || undefined,
    user_category_id: categoryId,
  });

  const list = page?.data ?? [];

  const [createBand, { isLoading: creating }] =
    useCreateMembershipBandMutation();
  const [updateBand, { isLoading: updating }] =
    useUpdateMembershipBandMutation();
  const [delBand, { isLoading: deleting }] = useDeleteMembershipBandMutation();

  const reset = () => {
    setEditing(null);
    setForm(empty);
  };

  const startEdit = (x: MembershipBand) => {
    setEditing(x);
    setForm({
      user_category_id: x.user_category_id,
      label: x.label,
      min_years: x.min_years,
      max_years: x.max_years ?? null,
      is_active: !!x.is_active,
    });
  };

  const onSubmit = async () => {
    if (!form.user_category_id) return alert("Select a user category.");
    if (!form.label?.trim()) return alert("Label is required.");

    const payload = {
      user_category_id: Number(form.user_category_id),
      label: String(form.label).trim(),
      min_years: Number(form.min_years),
      max_years:
        form.max_years === "" || form.max_years === null
          ? null
          : Number(form.max_years),
      is_active: !!form.is_active,
    };

    if (editing) {
      await updateBand({ id: editing.id, body: payload }).unwrap();
    } else {
      await createBand(payload).unwrap();
    }

    reset();
    refetch();
  };

  const onDelete = async (id: number) => {
    if (!window.confirm("Delete this membership band?")) return;
    await delBand(id).unwrap();
    refetch();
  };

  const filtered = useMemo(() => list, [list]);

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="text-xl font-semibold">Membership Bands</div>
          <div className="text-sm text-gray-500">
            Manage bands like 0–10 years, 11–15 years, etc.
          </div>
        </div>

        <button
          className="border rounded-md px-3 py-2 text-sm"
          onClick={() => refetch()}
        >
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-2">
        <input
          className="border rounded-md px-3 py-2 w-full md:w-80"
          placeholder="Search label..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        <select
          className="border rounded-md px-3 py-2 w-full md:w-72"
          value={categoryId ?? ""}
          onChange={(e) =>
            setCategoryId(e.target.value ? Number(e.target.value) : undefined)
          }
        >
          <option value="">All categories</option>
          {categories?.map((c: any) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <button className="border rounded-md px-3 py-2" onClick={reset}>
          Clear Form
        </button>
      </div>

      {/* Form */}
      <div className="border rounded-lg p-4 space-y-3">
        <div className="text-sm font-semibold text-gray-700">
          {editing ? "Edit Membership Band" : "Create Membership Band"}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <select
            className="border rounded-md px-3 py-2 text-sm md:col-span-2"
            value={form.user_category_id}
            onChange={(e) =>
              setForm((p: any) => ({
                ...p,
                user_category_id: Number(e.target.value),
              }))
            }
          >
            <option value={0}>Select category</option>
            {categories?.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <input
            className="border rounded-md px-3 py-2 text-sm md:col-span-2"
            placeholder="Label (e.g. 0-10 years)"
            value={form.label}
            onChange={(e) =>
              setForm((p: any) => ({ ...p, label: e.target.value }))
            }
          />

          <input
            type="number"
            className="border rounded-md px-3 py-2 text-sm"
            placeholder="Min years"
            value={form.min_years}
            onChange={(e) =>
              setForm((p: any) => ({ ...p, min_years: Number(e.target.value) }))
            }
          />

          <input
            type="number"
            className="border rounded-md px-3 py-2 text-sm"
            placeholder="Max years (optional)"
            value={form.max_years ?? ""}
            onChange={(e) =>
              setForm((p: any) => ({
                ...p,
                max_years:
                  e.target.value === "" ? null : Number(e.target.value),
              }))
            }
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            className="border rounded-md px-3 py-2 text-sm"
            onClick={reset}
          >
            Cancel
          </button>
          <button
            className="bg-black text-white rounded-md px-3 py-2 text-sm"
            onClick={onSubmit}
            disabled={creating || updating}
          >
            {creating || updating ? "Saving..." : editing ? "Update" : "Create"}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="border rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">
          <div className="col-span-4">Category</div>
          <div className="col-span-4">Label</div>
          <div className="col-span-2">Min</div>
          <div className="col-span-2">Max</div>
          <div className="col-span-12 md:col-span-2 text-right">Actions</div>
        </div>

        {isLoading ? (
          <div className="p-4 text-sm text-gray-500">Loading...</div>
        ) : error ? (
          <div className="p-4 text-sm text-red-600">
            Failed to load.
            <button className="underline ml-2" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        ) : filtered.length ? (
          filtered.map((x) => (
            <div
              key={x.id}
              className="grid grid-cols-12 px-3 py-2 border-t text-sm items-center"
            >
              <div className="col-span-4">
                {x.category?.name ?? `#${x.user_category_id}`}
              </div>
              <div className="col-span-4 font-medium">{x.label}</div>
              <div className="col-span-2">{x.min_years}</div>
              <div className="col-span-2">{x.max_years ?? "—"}</div>
              <div className="col-span-12 md:col-span-2 flex justify-end gap-2">
                <button className="text-blue-600" onClick={() => startEdit(x)}>
                  Edit
                </button>
                <button
                  className="text-red-600"
                  onClick={() => onDelete(x.id)}
                  disabled={deleting}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="p-4 text-sm text-gray-500">
            No membership bands yet.
          </div>
        )}
      </div>
    </div>
  );
}
